/**
 * Centralized localStorage key constants.
 * Replaces scattered string literals across the codebase.
 */

// Auth-related keys (cleared on signOut via clearAuth())
export const AUTH_KEYS = {
  /** Admin-As-Student / Admin-As-Instructor impersonation state */
  SFID: '_sfid',
  /** User's locale preference */
  APP_LOCALE: 'appLocale',
  /** Loaded locale flag (removed on auth init) */
  LOADED_LOCALE: 'loadedLocale',
  /** LMS locale */
  LMS_LOCALE: 'lmsLocale',
  /** God-mode flag for admin debugging */
  GOD_MODE: 'gm',
} as const

/** Cognito token key prefix pattern — all keys starting with this are auth-related */
export const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider.'

// Feature flag keys
export const FEATURE_KEYS = {
  /** Base64-encoded JSON of feature flags (permissions) */
  STORED_FF: 'stored-ff',
} as const

// Portal selection keys (preserved across logout)
export const PORTAL_KEYS = {
  SELECTED_MATERIAL: 'portal-selected-material',
  SELECTED_PROGRAM: 'portal-selected-program',
  SELECTED_REGISTRATION: 'portal-selected-registration',
} as const

// UI preference keys (preserved across logout)
export const UI_KEYS = {
  AUTO_PLAY_VIDEO: 'autoPlayVideo',
  ACTIVE_OPT: 'activeOPT',
  ACTIVE_OPT_REG_ID: 'activeOPTRegId',
  ACTIVE_LP_ID: 'activeLPID',
  ACTIVE_CT_ID: 'activeCTID',
  CT_REFRESH_REF: 'CTrefreshREF',
} as const

/**
 * Build a user-scoped localStorage key.
 * Pattern: `${sfId}-${suffix}` (e.g., "12345-lpid", "12345-loginTime")
 */
export const userKey = (sfId: string, suffix: string): string => `${sfId}-${suffix}`

export const USER_SUFFIXES = {
  LP_ID: 'lpid',
  PROGRAM_ID: 'programid',
  UNIT_ID: 'unitid',
  COMPLETED_LCS: 'completedLCS',
  REDO_LCS: 'redoLCS',
  SELECT_MOST_RECENT_LP: 'selectMostRecentLP',
  LOGIN_TIME: 'loginTime',
} as const

/**
 * Build a learning path scoped localStorage key.
 * Pattern: `lp:${userId}:${lpId}:${suffix}`
 */
export const lpKey = (userId: string, lpId: string, suffix: string): string =>
  `lp:${userId}:${lpId}:${suffix}`

/**
 * Build a learning path active hash key.
 * Pattern: `lp:${sfId}:activeHash`
 */
export const lpHashKey = (sfId: string): string => `lp:${sfId}:activeHash`
